import React, { useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import window from 'window-or-global';
import { ReactReduxContext } from 'react-redux';
import { ConnectedRouter } from 'connected-react-router';
import { nativeEditorComponentsPreloader } from '@confluence-classic/confluence-fabric-editor/src/lib';
import ChangeboardingManager from '@confluence-classic/confluence-changeboarding/src/ChangeboardingManager'; // 240KB
import LegacyBridge from '@confluence-classic/confluence-utils/src/components/LegacyBridge/LegacyBridgeContainer';

import { addUFOCustomData } from '@atlaskit/react-ufo/custom-data';
import { addUFOCustomCohortData } from '@atlaskit/react-ufo/custom-cohort-data';

import { PostOfficeRouterListener } from '@atlassian/confluence-post-office-provider';
import { isEmbeddedConfluence_DO_NOT_USE } from '@atlassian/embedded-confluence/isEmbeddedConfluence';

import { ConfluenceConversationAssistant } from '@confluence/conversation-assistant/entry-points/main';
import { SSRRenderFailureNotification } from '@confluence/ssr-render-failure-notification';
import { ConnectSupport } from '@confluence/connect-support';
import { RouteManager } from '@confluence/route-manager/entry-points/RouteManager';
import { useNavigationPolicy } from '@confluence/route-manager/entry-points/useNavigationPolicy';
import { isViewPageLite } from '@confluence/view-page/entry-points/isViewPageLite';
import { namedRoutes } from '@confluence/named-routes/entry-points/named-routes';
import { isEditPage } from '@confluence/named-routes/entry-points/routes-utils';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary';
import {
	EMBEDDED_EDIT_FMP_ACTION,
	EMBEDDED_EDIT_TTI_ACTION,
} from '@confluence/browser-metrics/entry-points/metric-keys';
import { TimeToAppIdle } from '@confluence/browser-metrics/entry-points/TimeToAppIdle';
import { BMEventBus } from '@confluence/browser-metrics/entry-points/BMEventBus';
import { markCustomTransitionExperienceForRoute } from '@confluence/browser-metrics/entry-points/utils';
import { startBrowserMetricsPageLoad } from '@confluence/browser-metrics/entry-points/startBrowserMetricsPageLoad';
import { setUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId';
import { CopySpacePermissionsForLegacyNewSpace } from '@confluence/copy-space-permissions/entry-points/CopySpacePermissionsForLegacyNewSpace';
import { Flags } from '@confluence/flags/entry-points/Flags';
import { CreateDialogContextProvider } from '@confluence/create-dialog-context';
import { Dialogs } from '@confluence/dialogs/entry-points/Dialogs';
import { NetworkStatusListener } from '@confluence/network-status-listener';
import { HighlightActionsProviderForNav3 } from '@confluence/highlight-actions/entry-points/HighlightActionsProvider';
import {
	debuggerEnabled,
	clearAnalyticsDebugger,
} from '@confluence/analytics-debugger/entry-points/AnalyticsDebugger';
import { getBrowserHistory, setBrowserHistoryListener } from '@confluence/browser-history';
import { startExperienceForRoute } from '@confluence/experience-tracker/entry-points/startExperienceForRoute';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient';
import { releaseSSRedLegacyMacros } from '@confluence/content-renderer-legacy-macros/entry-points/SSRedLegacyMacro';
import { ExternalShareContextProvider } from '@confluence/external-share-context/entry-points/ExternalShareContextProvider';
import { Heartbeat } from '@confluence/heartbeat';
import { EditPageLoadingContextProvider } from '@confluence/editor-loading-context/entry-points/EditPageLoadingContext';
import { LocalStorageQuotaSubscriber } from '@confluence/local-storage-quota-subscriber';
import { useInitializeThemeStateless } from '@confluence/theming/entry-points/useInitializeThemeStateless';
import { NotificationLandedEvent } from '@confluence/notifications/entry-points/NotificationLandedEvent';
import { ExperienceActionMeasures } from '@confluence/action-measures/entry-points/ExperienceActionMeasures';
import { SubscriptionsContextProvider } from '@confluence/graphql/entry-points/SubscriptionsContextProvider';
import { resetApolloStats } from '@confluence/graphql/entry-points/createClient';
import { preloadQueryBasedOnRoute, preloadExtensionsData } from '@confluence/query-preloaders';
import { StrictMode } from '@confluence/strict-mode';
import { useIsDesktop } from '@confluence/desktop-native-bridge';
import { fg } from '@confluence/feature-gating';
import { mobileAppTheme } from '@confluence/mobile-app-detection/entry-points/theme';
import { useIsMobileApp } from '@confluence/mobile-app-detection/entry-points/MobileAppDetection';
import { PreloadingMode } from '@confluence/hover-preloader';
import { getGlobalTransitionPreloader } from '@confluence/route-manager/entry-points/getGlobalTransitionPreloader';
import { QueryBoundaryNotification } from '@confluence/streaming-boundary-error-notification';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery';

import { routeComponents, NotFound } from '../Routes';
import { setMemoryMonitorRouteName } from '../../memory-monitor';
import { LazyCreateDialogQueryRenderer } from '../CreateDialogQueryRenderer';
import { LazyCreateDialogBlueprintWizardRenderer } from '../PseudoWizardCreateDialog';
import { preloadLivePagesComponents } from '../../preloaders';
import SingleModalContainer from '../SingleModal';
import LegacyCssOverrides from '../../tech-debt/LegacyCssOverrides';
import ShortcutsProvider from '../KeyboardShortcuts'; // 0KB
import EngagementMessages from '../EngagementMessages';
import { preloadComponentsNoStatsigSupport } from '../../route-component-preloader';
// This is tangled with fabric editor state that we can't remove for now.
import ErrorContainer from '../ReduxError';

const browserHistory = getBrowserHistory();

/**
 * Note: this wont run on legacy .action routes
 * if you are adding a global component that needs to work on legacy routes, you may need to add it to StandaloneConfluenceNavigation as well
 */

export function App({ sessionData }) {
	window.performance.mark('CFP-63.app-component-render');

	const { routePolicy } = useNavigationPolicy();
	const { isDesktop } = useIsDesktop();
	const { isMobileApp } = useIsMobileApp();
	const { isLoggedIn, isRovoLLMEnabled, accessStatus } = useSessionData();
	const isRovoEnabled =
		isRovoLLMEnabled && isLoggedIn && accessStatus !== AccessStatus.EXTERNAL_COLLABORATOR_ACCESS;

	// When loading confluence from the mobile app, we need the theme to respect the system color mode of the app
	const initialTheme = isMobileApp ? mobileAppTheme : undefined;

	// Initializing the theme in a non-blocking way, instead of using <ThemeContainer>, so
	// that the app does not flash and re-paint after the SPA loads on top of the SSR page.
	useInitializeThemeStateless(initialTheme);

	// Lite Mode™ check outside of the router
	// For certain pages supporting lite mode some functionality is not needed
	const isLite = isViewPageLite();

	// Check if app is being loaded in iFrame
	// We don't want to render the Rovo Chat wrapper if in iFrame
	const isIframe = typeof window !== 'undefined' && window.self !== window.top;

	const startPageLoadMetrics = useCallback(
		(routeName = '', ufoName = routeName, transitionId, contentId = '') => {
			startBrowserMetricsPageLoad(routeName, ufoName, transitionId, contentId);
			if (fg('platform_ufo_custom_cohort_data_migration')) {
				addUFOCustomCohortData('isDesktop', isDesktop);
			} else {
				addUFOCustomData({ isDesktop });
			}
		},
		[isDesktop],
	);

	const onRoute = useCallback(
		async (match, transitionId) => {
			// Reset window.__APOLLO_STATS__ on transition
			resetApolloStats();

			// transitionId = 0 when it is initial load, and 0 is falsey
			if (transitionId) {
				// The page can't be rendered by SSR if is a transition
				window['__SSR_RENDERED__'] = false;
				//hydrateRoot and related logic should not be enabled on transitions
				//as its only for ssr rendered page
				window['__HYDRATABLE__'] = false;
				releaseSSRedLegacyMacros();
				// Delayed content redirection is not needed on transitions
				window['__DELAYED_CONTENT_REDIRECTION__'] = {};
				if (debuggerEnabled) {
					clearAnalyticsDebugger();
				}
			} else {
				window.performance.mark('CFP-63.route-manager-initialized');
			}
			setUniquePageLoadId(match?.name ?? '', match, transitionId, sessionData);

			startExperienceForRoute(match, transitionId, sessionData);

			setMemoryMonitorRouteName(match?.name ?? 'unknown');

			void BMEventBus.stream([{ type: 'page-transition', ID: transitionId }]);

			const transitionPreloaderCurrentCallbackIDs =
				getGlobalTransitionPreloader().getCurrentRouteCallbackIds();
			const isSeamlessTransition = fg('confluence_frontend_transition_preloading_system')
				? transitionPreloaderCurrentCallbackIDs.includes(
						'live_doc_preloading_transition_callback_id',
					) && transitionId !== 0
				: false;

			//if the new editor metrics FF is on, start BM here only if the route is not edit page or if it is initial load, or an embedded editor.
			if (
				!isSeamlessTransition &&
				(fg('ccint-6065-fix-embedded-editor-metrics')
					? !isEditPage(match?.name) || transitionId === 0
					: !isEditPage(match?.name) || transitionId === 0 || isEmbeddedConfluence_DO_NOT_USE())
			) {
				startBrowserMetricsPageLoad(
					match?.name ?? '',
					match?.ufoName,
					transitionId,
					match?.params.contentId,
				);

				if (fg('platform_ufo_custom_cohort_data_migration')) {
					addUFOCustomCohortData('isDesktop', isDesktop);
				} else {
					addUFOCustomData({ isDesktop });
				}

				//If it is an embedded editor, start the BM right here since it does a full page reload
				if (isEditPage(match?.name) && isEmbeddedConfluence_DO_NOT_USE()) {
					ExperienceActionMeasures.markMeasureStart(EMBEDDED_EDIT_FMP_ACTION);
					ExperienceActionMeasures.markMeasureStart(EMBEDDED_EDIT_TTI_ACTION);
				}
			}

			// With fg('ccint-6065-fix-embedded-editor-metrics') enabled:
			// Start embedded editor BM for initial load and transitions here
			if (
				isEditPage(match?.name) &&
				isEmbeddedConfluence_DO_NOT_USE() &&
				fg('ccint-6065-fix-embedded-editor-metrics')
			) {
				ExperienceActionMeasures.markMeasureStart(EMBEDDED_EDIT_FMP_ACTION);
				ExperienceActionMeasures.markMeasureStart(EMBEDDED_EDIT_TTI_ACTION);
			}

			// add SSR stats to initial page load only
			// we will be utilizing serverTime to track BE perf in Statsig
			if (transitionId === 0) {
				/** @type {Object<string, {startTime: number, duration: number} | undefined>} */
				const ssrMeasure = window['__SSR_MEASURES__'];
				const serverTime = ssrMeasure
					? Object.keys(ssrMeasure)
							.filter((key) => key.startsWith('ssr-app/prepare/preloadQuery/fetch:'))
							.reduce((total, key) => total + (ssrMeasure[key]?.duration ?? 0), 0)
					: 0;
				/** @type {number | undefined} */
				const serverCallCount = window['__FETCH_COUNT__'];

				if (serverTime && serverCallCount) {
					addUFOCustomData({ serverTime, serverCallCount });
				}
			}

			// mark edit experience after transition to new route is complete
			markCustomTransitionExperienceForRoute(match?.name);

			getMonitoringClient().updateContext({
				pageName: match?.name ?? 'unknown',
				transitionId,
				product: isEmbeddedConfluence_DO_NOT_USE() ? 'embeddedConfluence' : 'confluence',
			});
		},
		[sessionData, isDesktop],
	);

	/**
	 * Please note this App is a pure component.
	 * This means following components will NOT re-render when user navigates.
	 * Please be careful when adding any component that requires router information.
	 */
	return (
		<StrictMode>
			<ErrorBoundary attribution={Attribution.BACKBONE}>
				<ChangeboardingManager>
					<SSRRenderFailureNotification />
					<QueryBoundaryNotification />
					<ConnectedRouter history={browserHistory} context={ReactReduxContext}>
						{/* Components above this line do NOT re-render in SPA transition */}
						<Fragment>
							<LegacyBridge />
							{/* This needs be placed before content */}
							<ExternalShareContextProvider path={browserHistory.location.pathname}>
								<CreateDialogContextProvider>
									{/* These preloaders need to be passed here to avoid circular dependencies in full page editor */}
									<EditPageLoadingContextProvider
										nativeEditorComponentsPreloader={nativeEditorComponentsPreloader}
										livePageComponentsPreloader={preloadLivePagesComponents}
									>
										<SubscriptionsContextProvider>
											<RouteManager
												routeComponents={routeComponents}
												namedRoutes={namedRoutes}
												notFound={NotFound}
												onInitialize={onRoute}
												onTransition={onRoute}
												onPreload={onPreloadRoute}
												routePolicy={routePolicy}
												setBrowserHistoryListener={setBrowserHistoryListener}
												startPageLoadMetrics={startPageLoadMetrics}
											>
												{/* Components that relies on RoutesContext */}
												<LegacyCssOverrides />
												<Heartbeat />
												<NotificationLandedEvent />
												<TimeToAppIdle />
												{fg('post-office-new-confluence-router-listener') && (
													<PostOfficeRouterListener />
												)}
												{!isLite && (
													<>
														<Dialogs />
														<LazyCreateDialogQueryRenderer />
														<LazyCreateDialogBlueprintWizardRenderer />
													</>
												)}
												{/* Rendering Rovo Chat without its UI to initialize the Global Rovo Store.
												ConfluenceConversationAssistant leverages the Router push function to handle internal navigation
												thus it is dependent on the Router and needs to be rendered within the RouteManager.
												*/}
												{!isIframe && isRovoEnabled && fg('aifc_create_enabled') && (
													<ConfluenceConversationAssistant>
														<></>
													</ConfluenceConversationAssistant>
												)}
											</RouteManager>
										</SubscriptionsContextProvider>
									</EditPageLoadingContextProvider>
								</CreateDialogContextProvider>
							</ExternalShareContextProvider>
							<HighlightActionsProviderForNav3 />
							<SingleModalContainer />
							<NetworkStatusListener />
						</Fragment>
						{/* Components after this line do NOT re-render in SPA transition */}
					</ConnectedRouter>
					<ShortcutsProvider />
					<ErrorContainer />
					<ConnectSupport />
					<Flags />
					<LocalStorageQuotaSubscriber />

					{!isLite && (
						<>
							<CopySpacePermissionsForLegacyNewSpace />
							<EngagementMessages />
						</>
					)}
				</ChangeboardingManager>
			</ErrorBoundary>
		</StrictMode>
	);
}

App.propTypes = {
	sessionData: PropTypes.object.isRequired,
};

function onPreloadRoute({ url }, previousMatch, options) {
	void preloadComponentsNoStatsigSupport(url, true);

	if (options?.preloadingMode === PreloadingMode.onlyJS) return;

	void preloadQueryBasedOnRoute(url, true, previousMatch, options);

	if (options?.preloadingMode === PreloadingMode.onlyJSAndCriticalData) return;

	void preloadExtensionsData(url);
}
